/**
 * Phase 3c §4 — Laguna ops queue for employer_access_requests.
 *
 * Read-only list with status filter. Ops add/close by using the admin UI
 * on the employer's portal; this page exists for visibility of the
 * reach-out pipeline.
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { adminAccessRequests } from "../lib/api";
import { track } from "../lib/analytics";

const STATUSES = ["", "admin_notified", "pending_admin", "admin_completed", "expired"];

export default function AdminAccessRequests() {
    const { t } = useTranslation();
    const [status, setStatus] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        track("admin_access_requests_view", {});
        document.title = t("auth.adminAccessReq.title");
    }, [t]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setErr("");
        adminAccessRequests({ status: status || null, limit: 100 })
            .then((res) => {
                if (!cancelled) setRows(res.items || []);
            })
            .catch((e) => {
                if (!cancelled) setErr(e?.response?.data?.detail || "Failed to load.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [status]);

    return (
        <section className="lg-section" data-testid="admin-access-requests-page" style={{ padding: "2rem" }}>
            <div className="section-label mono">{t("auth.adminAccessReq.h1")}</div>
            <h1 style={{ fontFamily: "'Fraunces', serif", marginBottom: "0.5rem" }}>
                {t("auth.adminAccessReq.h1")}
            </h1>
            <p style={{ color: "var(--muted)", maxWidth: "640px", marginBottom: "1.5rem" }}>
                {t("auth.adminAccessReq.sub")}
            </p>

            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem" }}>
                {STATUSES.map((s) => (
                    <button
                        key={s || "all"}
                        type="button"
                        onClick={() => setStatus(s)}
                        data-testid={`filter-${s || "all"}`}
                        style={{
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: "0.7rem",
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            padding: "0.5rem 0.85rem",
                            border: `1px solid ${status === s ? "var(--ink)" : "rgba(10,10,10,0.15)"}`,
                            background: status === s ? "var(--ink)" : "transparent",
                            color: status === s ? "var(--paper)" : "var(--ink)",
                            borderRadius: "999px",
                            cursor: "pointer",
                        }}
                    >
                        {s || t("auth.adminAccessReq.filterAll")}
                    </button>
                ))}
            </div>

            {err && <div className="apply-err" data-testid="admin-access-requests-err">{err}</div>}

            {loading ? (
                <div className="mono" style={{ color: "var(--muted)" }}>Loading…</div>
            ) : rows.length === 0 ? (
                <div className="mono" style={{ color: "var(--muted)" }} data-testid="admin-access-requests-empty">
                    {t("auth.adminAccessReq.empty")}
                </div>
            ) : (
                <table
                    data-testid="admin-access-requests-table"
                    style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "0.92rem",
                    }}
                >
                    <thead>
                        <tr style={{ borderBottom: "1px solid rgba(10,10,10,0.2)" }}>
                            <th style={thStyle}>{t("auth.adminAccessReq.colRequester")}</th>
                            <th style={thStyle}>{t("auth.adminAccessReq.colEmployer")}</th>
                            <th style={thStyle}>{t("auth.adminAccessReq.colAdmin")}</th>
                            <th style={thStyle}>{t("auth.adminAccessReq.colStatus")}</th>
                            <th style={thStyle}>{t("auth.adminAccessReq.colCreated")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.request_id} style={{ borderBottom: "1px solid rgba(10,10,10,0.08)" }}>
                                <td style={tdStyle}>
                                    <div>{r.requester?.name}</div>
                                    <div className="mono" style={{ color: "var(--muted)", fontSize: "0.72rem" }}>
                                        {r.requester?.email_lower}
                                    </div>
                                    {r.requester?.role && (
                                        <div className="mono" style={{ color: "var(--muted)", fontSize: "0.72rem" }}>
                                            {r.requester.role}
                                        </div>
                                    )}
                                </td>
                                <td style={tdStyle}>{r.target_employer_name}</td>
                                <td style={tdStyle} className="mono">
                                    {r.target_admin_email || "—"}
                                </td>
                                <td style={tdStyle}>
                                    <span className="mono" style={{ fontSize: "0.72rem" }}>
                                        {r.status}
                                    </span>
                                </td>
                                <td style={tdStyle}>
                                    {r.created_at && new Date(r.created_at).toLocaleString()}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </section>
    );
}

const thStyle = {
    textAlign: "left",
    padding: "0.75rem 0.5rem",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.7rem",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "var(--muted)",
    fontWeight: "normal",
};

const tdStyle = {
    padding: "0.85rem 0.5rem",
    verticalAlign: "top",
};
